import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Table, Text, Tooltip, UnstyledButton } from "@mantine/core"
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import type { IPlatformTenantRow } from "../../../../lib/api/platform-billing"
import { formatInteger } from "../../../../lib/format-date"
import { formatPct, formatUsd, profitTone } from "../format"
import { DEFAULT_SORT_DIR, DEFAULT_SORT_KEY, sortRows } from "./helpers"
import type { IProps, TSortDir, TSortKey } from "./interfaces"
import classes from "./styles.module.css"

interface ISortHeaderProps {
  label: React.ReactNode
  sortBy: TSortKey
  active: boolean
  dir: TSortDir
  numeric?: boolean
  onSort: (key: TSortKey) => void
}

function SortHeader({ label, sortBy, active, dir, numeric = false, onSort }: ISortHeaderProps): React.JSX.Element {
  const icon = !active ? (
    <IconSelector size={14} className={classes.sortIcon} aria-hidden />
  ) : dir === "asc" ? (
    <IconChevronUp size={14} className={classes.sortIcon} aria-hidden />
  ) : (
    <IconChevronDown size={14} className={classes.sortIcon} aria-hidden />
  )
  return (
    <Table.Th className={numeric ? classes.numericTh : undefined}>
      <UnstyledButton className={classes.sortButton} data-numeric={numeric} onClick={() => onSort(sortBy)}>
        <span>{label}</span>
        {icon}
      </UnstyledButton>
    </Table.Th>
  )
}

function TenantRow({ row }: { row: IPlatformTenantRow }): React.JSX.Element {
  const { i18n } = useLingui()
  const tone = profitTone(row.marginUsd)
  return (
    <Table.Tr>
      <Table.Td>
        <Tooltip label={row.tenantId} withArrow>
          <Text size="sm" className={classes.tenantName}>
            {row.name && row.name.trim() !== "" ? row.name : i18n._(msg`Unknown company`)}
          </Text>
        </Tooltip>
      </Table.Td>
      {/* This period — windowed money */}
      <Table.Td className={classes.numericTd}>{formatInteger(i18n, row.callCount)}</Table.Td>
      <Table.Td className={classes.numericTd}>{formatUsd(row.platformCostUsd)}</Table.Td>
      <Table.Td className={classes.numericTd}>{formatUsd(row.billableUsd)}</Table.Td>
      <Table.Td className={classes.numericTd}>
        <Text size="sm" fw={600} className={classes.money} data-tone={tone}>
          {formatUsd(row.marginUsd)}
        </Text>
      </Table.Td>
      <Table.Td className={classes.numericTd}>
        <Text size="sm" className={classes.money} data-tone={tone}>
          {formatPct(row.marginPct)}
        </Text>
      </Table.Td>
      {/* Lifetime — cumulative credits */}
      <Table.Td className={classes.numericTd} data-scope="lifetime">
        {formatInteger(i18n, row.creditsGranted)}
      </Table.Td>
      <Table.Td className={classes.numericTd} data-scope="lifetime">
        {formatInteger(i18n, row.creditsSpent)}
      </Table.Td>
      <Table.Td className={classes.numericTd} data-scope="lifetime">
        {formatInteger(i18n, row.creditBalance)}
      </Table.Td>
      <Table.Td className={classes.numericTd} data-scope="lifetime">
        <Text size="xs" c="dimmed">
          {formatUsd(row.creditBalanceUsd)}
        </Text>
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * The per-tenant billing table — pure presentation. Owns only its sort state
 * (a table-local UI concern); all data arrives via props. Column GROUPS make the
 * two time semantics explicit: "This period" holds the date-windowed money
 * (COGS / revenue / profit), "Lifetime" holds the cumulative credit-wallet
 * figures (a balance is a snapshot, not a window sum).
 */
export const PlatformTenantTable = ({ rows }: IProps): React.JSX.Element => {
  // 1. STATE
  const [sortKey, setSortKey] = useState<TSortKey>(DEFAULT_SORT_KEY)
  const [sortDir, setSortDir] = useState<TSortDir>(DEFAULT_SORT_DIR)

  // 3. DERIVED STATE
  const sorted = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir])

  // 4. CALLBACKS
  const handleSort = (key: TSortKey): void => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
      return
    }
    setSortKey(key)
    // Tenant id reads best A→Z; every money/credit column is most-interesting
    // (highest) first.
    setSortDir(key === "tenantId" || key === "name" ? "asc" : "desc")
  }

  // 7. RENDER
  return (
    <Table.ScrollContainer minWidth={1040}>
      <Table highlightOnHover verticalSpacing="sm" className={classes.table}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={classes.groupHead} data-scope="tenant">
              <Trans>Tenant</Trans>
            </Table.Th>
            <Table.Th className={classes.groupHead} colSpan={5} data-scope="period">
              <Trans>This period</Trans>
              <Text span size="xs" c="dimmed" fw={400} ml={6}>
                <Trans>money · date-windowed</Trans>
              </Text>
            </Table.Th>
            <Table.Th className={classes.groupHead} colSpan={4} data-scope="lifetime">
              <Trans>Lifetime</Trans>
              <Text span size="xs" c="dimmed" fw={400} ml={6}>
                <Trans>credits · cumulative</Trans>
              </Text>
            </Table.Th>
          </Table.Tr>
          <Table.Tr>
            <SortHeader
              label={<Trans>Company</Trans>}
              sortBy="name"
              active={sortKey === "name"}
              dir={sortDir}
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Calls</Trans>}
              sortBy="callCount"
              active={sortKey === "callCount"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>COGS</Trans>}
              sortBy="platformCostUsd"
              active={sortKey === "platformCostUsd"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Revenue</Trans>}
              sortBy="billableUsd"
              active={sortKey === "billableUsd"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Profit</Trans>}
              sortBy="marginUsd"
              active={sortKey === "marginUsd"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Margin</Trans>}
              sortBy="marginPct"
              active={sortKey === "marginPct"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Granted</Trans>}
              sortBy="creditsGranted"
              active={sortKey === "creditsGranted"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Spent</Trans>}
              sortBy="creditsSpent"
              active={sortKey === "creditsSpent"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Balance</Trans>}
              sortBy="creditBalance"
              active={sortKey === "creditBalance"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
            <SortHeader
              label={<Trans>Balance value</Trans>}
              sortBy="creditBalanceUsd"
              active={sortKey === "creditBalanceUsd"}
              dir={sortDir}
              numeric
              onSort={handleSort}
            />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.length === 0 ? (
            <Table.Tr>
              <Table.Td colSpan={10}>
                <Text c="dimmed" ta="center" py="md">
                  <Trans>No billing activity for this period.</Trans>
                </Text>
              </Table.Td>
            </Table.Tr>
          ) : (
            sorted.map((row) => <TenantRow key={row.tenantId} row={row} />)
          )}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  )
}
